import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoWhite from "@/assets/bullgrape_white-80.webp";
import logoWhite2x from "@/assets/bullgrape_white-160.webp";
import LanguageSwitcher from "./LanguageSwitcher";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { t } = useTranslation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = isHome
    ? [
        { label: t("nav.work"), href: "#work" },
        { label: t("nav.services"), href: "#services" },
        { label: t("nav.process"), href: "#process" },
        { label: t("nav.about"), href: "#about" },
        { label: t("nav.contact"), href: "#contact" },
      ]
    : [
        { label: t("nav.work"), href: "/#work" },
        { label: t("nav.services"), href: "/#services" },
        { label: t("nav.process"), href: "/#process" },
        { label: t("nav.about"), href: "/#about" },
        { label: t("nav.contact"), href: "/#contact" },
      ];

  return (
    <>
      <div className={`floating-nav-wrapper ${scrolled ? "nav-scrolled" : ""}`}>
        <nav className="floating-nav-inner">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 floating-nav-logo">
            <img
              src={logoWhite}
              srcSet={`${logoWhite} 1x, ${logoWhite2x} 2x`}
              alt="Bullgrape"
              width={40}
              height={34}
              decoding="async"
              className="w-10 h-auto shrink-0"
            />
            <span className="floating-nav-wordmark font-mono text-[12px] tracking-[0.35em] text-white uppercase hidden md:inline-block">
              BULLGRAPE<span className="text-accent">.</span>
            </span>
          </Link>

          <div className="floating-nav-links hidden md:flex items-center">
            {navLinks.map((link) =>
              isHome ? (
                <a
                  key={link.label}
                  href={link.href}
                  className="font-mono text-[12px] tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors whitespace-nowrap"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className="font-mono text-[12px] tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors whitespace-nowrap"
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          <div className="hidden md:flex items-center gap-3 shrink-0 floating-nav-right">
            {isHome ? (
              <a
                href="#contact"
                className="floating-nav-cta pill inline-flex items-center gap-2 bg-white text-background text-[12px] font-mono tracking-[0.15em] uppercase hover:bg-white/90 transition-colors whitespace-nowrap"
              >
                {t("nav.cta")} →
              </a>
            ) : (
              <Link
                to="/#contact"
                className="floating-nav-cta pill inline-flex items-center gap-2 bg-white text-background text-[12px] font-mono tracking-[0.15em] uppercase hover:bg-white/90 transition-colors whitespace-nowrap"
              >
                {t("nav.cta")} →
              </Link>
            )}
            <LanguageSwitcher />
          </div>

          <div className="flex items-center gap-3 md:hidden">
            <LanguageSwitcher />
            <button
              onClick={() => setOpen(!open)}
              className="font-mono text-[12px] tracking-wider uppercase text-white"
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? t("nav.close") : t("nav.menu")}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-background flex flex-col items-center justify-center gap-8"
          >
            {navLinks.map((link, i) => (
              <motion.div
                key={link.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                {isHome ? (
                  <a
                    href={link.href}
                    onClick={() => setOpen(false)}
                    className="text-3xl font-bold tracking-tight hover:text-accent transition-colors"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className="text-3xl font-bold tracking-tight hover:text-accent transition-colors"
                  >
                    {link.label}
                  </Link>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
